// dashboard.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Gauge,
  Cpu,
  Wrench,
  Layers,
  List,
  Settings,
  AlertTriangle,
  Monitor,
  Zap,
  Home,
  PlusCircle,
  Package,
  Activity,
  FileText,
  Calendar,
  Search,
  Trash2,
  Edit,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function MaintenanceDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("list-machine-category");
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // State for Add Machine form
  const [machineForm, setMachineForm] = useState({
    category: "",
    subCategory: "",
    newCategory: "",
    machineName: "",
    model: "",
    manufactureYear: "",
    maintenanceDate: "",
    nextMaintenanceDate: "",
    documents: []
  });

  // State for Machine List with categories
  const [machines, setMachines] = useState([
    {
      id: 1,
      machineName: "CNC Lathe Machine",
      model: "CNC-500",
      category: "Daily Maintenance",
      subCategory: "Precision Tools",
      manufactureYear: "2022",
      maintenanceDate: "2024-01-15",
      nextMaintenanceDate: "2024-01-16",
      status: "Operational",
      lastCheck: "2024-01-15 08:30 AM"
    },
    {
      id: 2,
      machineName: "Hydraulic Press",
      model: "HP-200T",
      category: "Weekly Maintenance",
      subCategory: "Heavy Machinery",
      manufactureYear: "2021",
      maintenanceDate: "2024-01-10",
      nextMaintenanceDate: "2024-01-17",
      status: "Under Maintenance",
      lastCheck: "2024-01-10 02:15 PM"
    },
    {
      id: 3,
      machineName: "Industrial Oven",
      model: "IO-850",
      category: "Monthly Maintenance",
      subCategory: "Thermal Equipment",
      manufactureYear: "2023",
      maintenanceDate: "2024-01-01",
      nextMaintenanceDate: "2024-02-01",
      status: "Operational",
      lastCheck: "2024-01-01 11:00 AM"
    },
    {
      id: 4,
      machineName: "Air Compressor",
      model: "AC-75HP",
      category: "Yearly Maintenance",
      subCategory: "Utility Equipment",
      manufactureYear: "2020",
      maintenanceDate: "2024-01-05",
      nextMaintenanceDate: "2025-01-05",
      status: "Needs Service",
      lastCheck: "2024-01-05 09:45 AM"
    }
  ]);

  // State for Add Gauge form
  const [gaugeForm, setGaugeForm] = useState({
    gaugeName: "",
    gaugeType: "",
    range: "",
    accuracy: "",
    calibrationDate: "",
    nextCalibrationDate: "",
    location: ""
  });

  const [gauges, setGauges] = useState([]);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Sidebar menu items
  const tabItems = [
    { id: "add-gauges", label: "Add Gauges", icon: <Gauge size={18} /> },
    { id: "list-machine-category", label: "List of machine with category", icon: <Cpu size={18} /> },
    { id: "list-tool-accessories", label: "List of Tool with Accessories", icon: <Wrench size={18} /> },
    { id: "list-instruments-category", label: "List of Instruments with category", icon: <Layers size={18} /> },
    { id: "list-instruments-gauges", label: "List of Instruments with Gauges", icon: <List size={18} /> },
    { id: "machine-maintenance", label: "Machine Maintenance", icon: <Settings size={18} /> },
    { id: "break-down-maintenance", label: "Break Down Maintenance", icon: <AlertTriangle size={18} /> },
    { id: "digital-equipment", label: "Digital equipment maintenance", icon: <Monitor size={18} /> },
    { id: "electrical-maintenance", label: "Electrical maintenance", icon: <Zap size={18} /> },
    { id: "list-accessories-category", label: "List of Accessaries with category", icon: <Package size={18} /> },
    { id: "floor-housekeeping", label: "Floor and house keeping", icon: <Home size={18} /> },
    { id: "add-machine", label: "Add machine", icon: <PlusCircle size={18} /> },
    { id: "add-tool", label: "Add Tool", icon: <Wrench size={18} /> },
    { id: "add-accessories", label: "Add Accessories", icon: <Package size={18} /> },
    { id: "add-instrument", label: "Add Instrument", icon: <Activity size={18} /> },
    { id: "break-down-maintenance-2", label: "Break Down Maintenance", icon: <AlertTriangle size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  // Handle Machine Form Input
  const handleMachineChange = (e) => {
    const { name, value } = e.target;
    setMachineForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle Gauge Form Input
  const handleGaugeChange = (e) => {
    const { name, value } = e.target;
    setGaugeForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle File Upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setMachineForm(prev => ({ ...prev, documents: [...prev.documents, ...files] }));
  };

  // Remove Document
  const removeDocument = (index) => {
    setMachineForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Add New Machine
  const handleAddMachine = (e) => {
    e.preventDefault();
    const newMachine = {
      id: machines.length + 1,
      machineName: machineForm.machineName,
      model: machineForm.model,
      category: machineForm.category || machineForm.newCategory,
      subCategory: machineForm.subCategory,
      manufactureYear: machineForm.manufactureYear,
      maintenanceDate: machineForm.maintenanceDate,
      nextMaintenanceDate: machineForm.nextMaintenanceDate,
      status: "Operational",
      lastCheck: new Date().toLocaleString()
    };
    setMachines([...machines, newMachine]);
    setMachineForm({
      category: "",
      subCategory: "",
      newCategory: "",
      machineName: "",
      model: "",
      manufactureYear: "",
      maintenanceDate: "",
      nextMaintenanceDate: "",
      documents: []
    });
    alert("Machine added successfully!");
  };

  // Add New Gauge
  const handleAddGauge = (e) => {
    e.preventDefault();
    const newGauge = {
      id: gauges.length + 1,
      ...gaugeForm,
      status: "Active",
      addedDate: new Date().toLocaleDateString()
    };
    setGauges([...gauges, newGauge]);
    setGaugeForm({
      gaugeName: "",
      gaugeType: "",
      range: "",
      accuracy: "",
      calibrationDate: "",
      nextCalibrationDate: "",
      location: ""
    });
    alert("Gauge added successfully!");
  };

  // Delete Machine
  const deleteMachine = (id) => {
    if (window.confirm("Are you sure you want to delete this machine?")) {
      setMachines(machines.filter(machine => machine.id !== id));
    }
  };

  // Filter Machines
  const filteredMachines = machines.filter(machine => {
    const matchesSearch = machine.machineName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          machine.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || machine.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Maintenance Categories for filter
  const maintenanceCategories = ["Daily Maintenance", "Weekly Maintenance", "Monthly Maintenance", "Yearly Maintenance"];

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case "Operational": return "#10b981";
      case "Under Maintenance": return "#f59e0b";
      case "Needs Service": return "#ef4444";
      default: return "#6b7280";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case "Operational": return <CheckCircle size={16} />;
      case "Under Maintenance": return <Clock size={16} />;
      case "Needs Service": return <AlertCircle size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "add-machine":
        return (
          <div className="maintenance-form-container">
            <div className="maintenance-form-header">
              <h2 className="maintenance-form-title">Add Machine</h2>
              <p className="maintenance-form-subtitle">Fill in the details to register a new machine</p>
            </div>
            <form onSubmit={handleAddMachine} className="maintenance-form">
              {/* Category Selection */}
              <div className="maintenance-form-group">
                <label className="maintenance-form-label">Select Category & Sub Category</label>
                <div className="maintenance-form-row">
                  <select
                    name="category"
                    value={machineForm.category}
                    onChange={handleMachineChange}
                    className="maintenance-form-select"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Daily Maintenance">Daily Maintenance</option>
                    <option value="Weekly Maintenance">Weekly Maintenance</option>
                    <option value="Monthly Maintenance">Monthly Maintenance</option>
                    <option value="Yearly Maintenance">Yearly Maintenance</option>
                  </select>
                  <input
                    type="text"
                    name="subCategory"
                    placeholder="Sub Category"
                    value={machineForm.subCategory}
                    onChange={handleMachineChange}
                    className="maintenance-form-input"
                  />
                </div>
              </div>

              {/* New Category Option */}
              <div className="maintenance-form-group">
                <label className="maintenance-form-label">New Category (Optional)</label>
                <input
                  type="text"
                  name="newCategory"
                  placeholder="Enter new category name"
                  value={machineForm.newCategory}
                  onChange={handleMachineChange}
                  className="maintenance-form-input"
                />
              </div>

              {/* Machine Details */}
              <div className="maintenance-form-row two-cols">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Machine Name</label>
                  <input
                    type="text"
                    name="machineName"
                    placeholder="Enter machine name"
                    value={machineForm.machineName}
                    onChange={handleMachineChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Model</label>
                  <input
                    type="text"
                    name="model"
                    placeholder="Enter model number"
                    value={machineForm.model}
                    onChange={handleMachineChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
              </div>

              <div className="maintenance-form-row two-cols">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Manufacture Year</label>
                  <input
                    type="number"
                    name="manufactureYear"
                    placeholder="YYYY"
                    value={machineForm.manufactureYear}
                    onChange={handleMachineChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Maintenance Date</label>
                  <input
                    type="date"
                    name="maintenanceDate"
                    value={machineForm.maintenanceDate}
                    onChange={handleMachineChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
              </div>

              <div className="maintenance-form-group">
                <label className="maintenance-form-label">Next Maintenance Date</label>
                <input
                  type="date"
                  name="nextMaintenanceDate"
                  value={machineForm.nextMaintenanceDate}
                  onChange={handleMachineChange}
                  className="maintenance-form-input"
                  required
                />
              </div>

              {/* Attach Documents */}
              <div className="maintenance-form-group">
                <label className="maintenance-form-label">Attach Documents</label>
                <div className="maintenance-file-upload">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="maintenance-file-input"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                  />
                  <div className="maintenance-file-list">
                    {machineForm.documents.map((doc, index) => (
                      <div key={index} className="maintenance-file-item">
                        <FileText size={16} />
                        <span>{doc.name}</span>
                        <button type="button" onClick={() => removeDocument(index)} className="maintenance-file-remove">
                          <XCircle size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <button type="submit" className="maintenance-submit-btn">
                <PlusCircle size={18} />
                Add Machine
              </button>
            </form>
          </div>
        );

      case "add-gauges":
        return (
          <div className="maintenance-form-container">
            <div className="maintenance-form-header">
              <h2 className="maintenance-form-title">Add Gauges</h2>
              <p className="maintenance-form-subtitle">Register new measuring instruments and gauges</p>
            </div>
            <form onSubmit={handleAddGauge} className="maintenance-form">
              <div className="maintenance-form-row two-cols">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Gauge Name</label>
                  <input
                    type="text"
                    name="gaugeName"
                    placeholder="Enter gauge name"
                    value={gaugeForm.gaugeName}
                    onChange={handleGaugeChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Gauge Type</label>
                  <select
                    name="gaugeType"
                    value={gaugeForm.gaugeType}
                    onChange={handleGaugeChange}
                    className="maintenance-form-select"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Pressure Gauge">Pressure Gauge</option>
                    <option value="Temperature Gauge">Temperature Gauge</option>
                    <option value="Flow Meter">Flow Meter</option>
                    <option value="Level Gauge">Level Gauge</option>
                    <option value="Digital Caliper">Digital Caliper</option>
                    <option value="Micrometer">Micrometer</option>
                  </select>
                </div>
              </div>

              <div className="maintenance-form-row two-cols">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Range</label>
                  <input
                    type="text"
                    name="range"
                    placeholder="e.g., 0-100 PSI"
                    value={gaugeForm.range}
                    onChange={handleGaugeChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Accuracy</label>
                  <input
                    type="text"
                    name="accuracy"
                    placeholder="e.g., ±0.5%"
                    value={gaugeForm.accuracy}
                    onChange={handleGaugeChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
              </div>

              <div className="maintenance-form-row two-cols">
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Calibration Date</label>
                  <input
                    type="date"
                    name="calibrationDate"
                    value={gaugeForm.calibrationDate}
                    onChange={handleGaugeChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
                <div className="maintenance-form-group">
                  <label className="maintenance-form-label">Next Calibration Date</label>
                  <input
                    type="date"
                    name="nextCalibrationDate"
                    value={gaugeForm.nextCalibrationDate}
                    onChange={handleGaugeChange}
                    className="maintenance-form-input"
                    required
                  />
                </div>
              </div>

              <div className="maintenance-form-group">
                <label className="maintenance-form-label">Location</label>
                <input
                  type="text"
                  name="location"
                  placeholder="Installation location"
                  value={gaugeForm.location}
                  onChange={handleGaugeChange}
                  className="maintenance-form-input"
                />
              </div>

              <button type="submit" className="maintenance-submit-btn">
                <PlusCircle size={18} />
                Add Gauge
              </button>
            </form>

            {/* Gauges List */}
            {gauges.length > 0 && (
              <div className="maintenance-list-section">
                <h3 className="maintenance-list-title">Registered Gauges</h3>
                <div className="maintenance-table-container">
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Gauge Name</th>
                        <th>Type</th>
                        <th>Range</th>
                        <th>Next Calibration</th>
                        <th>Location</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gauges.map(gauge => (
                        <tr key={gauge.id}>
                          <td>{gauge.gaugeName}</td>
                          <td>{gauge.gaugeType}</td>
                          <td>{gauge.range}</td>
                          <td>{gauge.nextCalibrationDate}</td>
                          <td>{gauge.location || "-"}</td>
                          <td>
                            <button className="maintenance-action-btn edit"><Edit size={16} /></button>
                            <button className="maintenance-action-btn delete" onClick={() => {
                              setGauges(gauges.filter(g => g.id !== gauge.id));
                            }}><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case "list-machine-category":
        return (
          <div className="maintenance-list-container">
            <div className="maintenance-list-header">
              <h2 className="maintenance-list-title">List of Machine with Category</h2>
              <div className="maintenance-list-filters">
                <div className="maintenance-search-box">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search machine..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="maintenance-search-input"
                  />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="maintenance-filter-select"
                >
                  <option value="all">All Categories</option>
                  {maintenanceCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category Cards */}
            <div className="maintenance-category-stats">
              {maintenanceCategories.map(cat => (
                <div key={cat} className="maintenance-stat-card" style={{ borderLeftColor: cat === "Daily Maintenance" ? "#10b981" : cat === "Weekly Maintenance" ? "#3b82f6" : cat === "Monthly Maintenance" ? "#f59e0b" : "#ef4444" }}>
                  <div className="maintenance-stat-icon">
                    {cat === "Daily Maintenance" ? <Clock size={24} /> : cat === "Weekly Maintenance" ? <Calendar size={24} /> : cat === "Monthly Maintenance" ? <Activity size={24} /> : <Settings size={24} />}
                  </div>
                  <div className="maintenance-stat-info">
                    <h4>{cat}</h4>
                    <p>{machines.filter(m => m.category === cat).length} Machines</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Machines Table */}
            <div className="maintenance-table-container">
              <table className="maintenance-table">
                <thead>
                  <tr>
                    <th>Machine Name</th>
                    <th>Model</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Manufacture Year</th>
                    <th>Next Maintenance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMachines.map(machine => (
                    <tr key={machine.id}>
                      <td className="maintenance-machine-name">{machine.machineName}</td>
                      <td>{machine.model}</td>
                      <td>
                        <span className={`maintenance-category-badge ${machine.category.toLowerCase().replace(' ', '-')}`}>
                          {machine.category}
                        </span>
                      </td>
                      <td>{machine.subCategory}</td>
                      <td>{machine.manufactureYear}</td>
                      <td>{machine.nextMaintenanceDate}</td>
                      <td>
                        <span className="maintenance-status-badge" style={{ backgroundColor: getStatusColor(machine.status) + '20', color: getStatusColor(machine.status) }}>
                          {getStatusIcon(machine.status)}
                          {machine.status}
                        </span>
                      </td>
                      <td>
                        <div className="maintenance-action-buttons">
                          <button className="maintenance-action-btn view"><Eye size={16} /></button>
                          <button className="maintenance-action-btn edit"><Edit size={16} /></button>
                          <button className="maintenance-action-btn delete" onClick={() => deleteMachine(machine.id)}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredMachines.length === 0 && (
                <div className="maintenance-empty-state">
                  <p>No machines found</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="maintenance-content-card">
            <h2 className="maintenance-page-title">{getPageTitle()}</h2>
            <p className="maintenance-page-description">Content for {getPageTitle()} will be displayed here.</p>
          </div>
        );
    }
  };

  // Get page title based on active tab
  const getPageTitle = () => {
    const tab = tabItems.find(t => t.id === activeTab);
    return tab ? tab.label : "Maintenance Dashboard";
  };

  return (
    <div className="maintenance-dashboard-container">
      {/* Sidebar */}
      <div className="maintenance-sidebar">
        <div className="maintenance-logo-container">
          <div className="maintenance-logo-icon">
            <img 
              src="/rahulerp.jpg" 
              alt="Company Logo" 
              className="maintenance-logo-image"
            />
          </div>
          <div>
            <h2 className="maintenance-company-name">Ragul Auto Tech</h2>
            <p className="maintenance-company-tagline">AI Powered ERP</p>
          </div>
        </div>

        <nav className="maintenance-nav-menu">
          {tabItems.map((tab) => (
            <button
              key={tab.id}
              className={`maintenance-nav-item ${activeTab === tab.id ? 'maintenance-nav-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="maintenance-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="maintenance-logout-container">
          <button onClick={handleLogoutClick} className="maintenance-logout-button">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="maintenance-main-content">
        {/* Header */}
        <header className="maintenance-header">
          <div>
            <h1 className="maintenance-main-title">Maintenance Dashboard</h1>
            <div className="maintenance-subtitle">
              Welcome, {user?.role || 'Maintenance Manager'}
            </div>
          </div>
          <div className="maintenance-header-actions">
            <div className="maintenance-last-updated">Last updated: {new Date().toLocaleString()}</div>
          </div>
        </header>
        <BackButton />

        {/* Dynamic Content */}
        {renderContent()}

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Maintenance</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}